import {
  DateRangeFilterConfig,
  FilterConfig,
  FilterGroupConfig,
  InputFilterConfig,
  SearchInputFilterConfig,
} from 'components/SearchAndFilters/FilterTypes'; // Assuming the `FilterConfig` type is defined in a separate file.
import {
  MultiSelectFilterConfig,
  MultiSelectRenderStyle,
} from 'components/SearchAndFilters/MultiSelect';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { fetchHosts } from 'lib/helper';
import React, { useEffect, useState } from 'react';
import { companyIdAtom } from 'views/state';

import { AgeFilterConfig } from './AgeFilter';
import { getAssetsFetchConfig, HostsFilterConfig } from './HostsFilter';
import {
  filterValuesAtom,
  isQuerystringFiltersInitializedAtom,
  parsedFilterValuesAtom,
  SourceType,
  sourceTypeAtom,
  sourceTypeConfig,
  useResetAllVulnerabilitiesAtoms,
} from './state';

export const useFilterConfig = (): FilterConfig => {
  const setQuerystringFiltersInitialized = useSetAtom(
    isQuerystringFiltersInitializedAtom,
  );
  const [_filterValues, setFilterValues] = useAtom(filterValuesAtom);
  const filterValues = useAtomValue(parsedFilterValuesAtom);
  // sourceTypeAtom
  const [sourceType, setSourceType] = useAtom(sourceTypeAtom);
  const company_id = useAtomValue(companyIdAtom);

  const [hostsData, setHostsData] = useState([]);
  const HOSTS_LIMIT = 100;

  // intermediate state to ensure filters from query string are updated before fetching data
  // hydrate with a default before reading
  useHydrateAtoms([
    [filterValuesAtom, { status: ['NEW', 'ACTIVE', 'REOPENED'] }],
  ] as const);
  const [queryFiltersUpdated, setQueryFiltersUpdated] = useState(false);
  const [storageItemsHydrated, setStorageItemsHydrated] = useState(false);

  const resetAllFilters = useResetAllVulnerabilitiesAtoms();

  useEffect(() => {
    // ensure the storage items are initialized before reading query string params
    setStorageItemsHydrated(true);
  }, []);

  // initial filter values from query string
  useEffect(() => {
    // exit if storage items are not hydrated yet, or if the query filters are already updated
    if (!storageItemsHydrated || queryFiltersUpdated) return;
    setQuerystringFiltersInitialized(false);

    const params = new URLSearchParams(window.location.search);
    // Accept empty string as a valid value for source_type
    const updatedSourceType = params.has('source_type')
      ? params.get('source_type') || ''
      : sourceType;

    // initQueryFilters returns queryFilters
    const initQueryFilters = async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const queryFilters: Record<string, any> = {};
      const entries = Array.from(params.entries());

      for (const [key, value] of entries) {
        if (key === 'source_type') {
          // do nothing, prevent adding to queryFilters
        } else if (key === 'host_ids') {
          const hostIds = (value ?? '').split(',').map(Number);
          const { baseUrl, additionalParams } = getAssetsFetchConfig(
            // instead of using the state, we can use the value from the query string
            // as state may get updated later
            updatedSourceType as SourceType,
            company_id,
            hostIds,
          );
          const assetsData = await fetchHosts({
            baseUrl: baseUrl,
            search: '',
            page: 0,
            limit: HOSTS_LIMIT,
            additionalParams: additionalParams,
            company_id: company_id,
          });
          if (assetsData) {
            const assets = assetsData?.results || [];
            setHostsData(assets);
            const selectedHosts = assets.filter((asset) =>
              hostIds.includes(asset.id),
            );
            queryFilters['host_ids'] = selectedHosts;
          }
        } else if (key.startsWith('pre_filter__')) {
          const filterKey = key.replace('pre_filter__', '');
          queryFilters['predefinedFilters'] = [`${filterKey}=${value}`];
        } else if (key.startsWith('dt_filter__')) {
          // expected format: dt_filter__<filter>__<rangeType>=<value>
          // e.g. dt_filter__due_date__from=2021-01-01
          const filter = key.replace('dt_filter__', '');
          const [filterKey, rangeType] = filter.split('__');
          const fromValue = rangeType === 'from' ? parseInt(value) : null;
          const toValue = rangeType === 'to' ? parseInt(value) : null;
          const filterValue = queryFilters[filterKey]
            ? queryFilters[filterKey]
            : { from: fromValue, to: toValue };
          queryFilters[filterKey] = [filterValue];
        } else if (key.startsWith('age__')) {
          // expected format: age__min=1, age__max=10
          const [filterKey, rangeType] = key.split('__');
          const ageValue = parseInt(value);
          const filterValue = queryFilters[filterKey]
            ? queryFilters[filterKey][0]
            : {
                minValue: undefined,
                maxValue: undefined,
                isMinExclusive: false,
                isMaxExclusive: false,
              };
          if (rangeType === 'min') {
            filterValue.minValue = ageValue;
          } else if (rangeType === 'max') {
            filterValue.maxValue = ageValue;
          } else if (rangeType === 'min_exclusive') {
            filterValue.minValue = ageValue;
            filterValue.isMinExclusive = true;
          } else if (rangeType === 'max_exclusive') {
            filterValue.maxValue = ageValue;
            filterValue.isMaxExclusive = true;
          } else {
            throw new Error(`Invalid age filter range type: ${rangeType}`);
          }

          queryFilters[filterKey] = [filterValue];
        } else {
          queryFilters[key] = value.split(',');
        }
      }

      return queryFilters;
    };

    initQueryFilters()
      .then((queryFilters) => {
        if (Object.keys(queryFilters).length > 0) {
          resetAllFilters();
          setSourceType(updatedSourceType);
          setFilterValues(queryFilters);
        }

        // update the local state, and wait for it before fetching data in next render cycle
        setQueryFiltersUpdated(true);
      })
      .catch((error) => {
        console.error('Error initializing query filters:', error);
      });

    // on unmount set the query filters initialized to false
    // so on next mount it gets re-evaluated
    // otherwise it will lead to multiple calls to the API
    return () => {
      setQuerystringFiltersInitialized(false);
    };
  }, [queryFiltersUpdated, storageItemsHydrated]);

  useEffect(() => {
    // Only set default if source type is SAST/ALL and issue_type is empty or default
    if (sourceType === SourceType.SAST || sourceType === SourceType.ALL) {
      // Only change if issue_type is empty or undefined
      if (!filterValues.issue_type) {
        setFilterValues((prevValues) => ({
          ...prevValues,
          issue_type: ['VULNERABILITY'],
        }));
      }
    }
  }, [sourceType, setFilterValues]);

  useEffect(() => {
    if (queryFiltersUpdated) {
      setQuerystringFiltersInitialized(true);
    }
  }, [queryFiltersUpdated, setQuerystringFiltersInitialized]);

  const config = React.useMemo(() => {
    const sourceTypeConfigData =
      sourceTypeConfig[sourceType] || sourceTypeConfig.default;

    const newConfig = new FilterConfig({
      searchFilterId: 'search',
      rightPanelFilters: [
        'severity',
        'status',
        'predefinedFilters',
        'issue_type',
        'age',
        'id',
      ],
      leftPanelFilters: ['host_ids', 'dateRangeFilters', 'miscFiltersGroup'],
      filters: [
        new SearchInputFilterConfig({
          id: 'search',
          label: 'Search',
          placeholder: 'Search and Filter',
          maxLength: 250,
          debounceTime: 1000,
          enableDebounce: true,
          inputProps: { type: 'text' },
          showInFilterMenu: false,
        }),
        new HostsFilterConfig({
          id: 'host_ids',
          label: sourceTypeConfigData.filterLabel,
          badgeLabel: sourceTypeConfigData.badgeLabel,
          tooltip: sourceTypeConfigData.tooltip,
          iconClass: sourceTypeConfigData.iconClass,
          enabled: sourceType !== SourceType.ALL,
          hostsData: hostsData,
        }),
        new FilterGroupConfig({
          id: 'miscFiltersGroup',
          label: 'Misc. Filters',
          iconClass: 'fas fa-filter', // Icon added
          tooltip:
            sourceType === SourceType.SAST
              ? 'Filter by Rule ID'
              : 'Filter by Qualys ID',
          enabled: sourceType !== SourceType.ALL,
          filters: [
            new InputFilterConfig({
              id: 'qid',
              label: 'QID',
              tooltip: 'Filter by Qualys Vulnerability ID',
              renderLabel: true,
              placeholder: ' ',
              maxLength: 50,
              debounceTime: 1000,
              enableDebounce: true,
              labelWidth: '65px',
              inputProps: { type: 'text', width: '50px', maxwidth: '100px' },
              enabled: sourceType !== SourceType.SAST,
            }),
            new InputFilterConfig({
              id: 'sonarqube_rule_id',
              label: 'Rule ID',
              renderLabel: true,
              placeholder: ' ',
              debounceTime: 1000,
              enableDebounce: true,
              labelWidth: '65px',
              inputProps: {
                type: 'number',
                max: 9999,
                maxLength: 4,
                onInput: (e: React.ChangeEvent<HTMLInputElement>) => {
                  if (e.target.value.length > 4) {
                    e.target.value = e.target.value.slice(0, 4);
                  }
                },
              },
              enabled: sourceType == SourceType.SAST,
            }),
          ],
        }),
        new FilterGroupConfig({
          id: 'dateRangeFilters',
          label: 'Filter by Date',
          iconClass: 'fas fa-calendar-alt',
          tooltip:
            'Filter by date and timestamps. The From and To dates are inclusive.',
          filters: [
            new DateRangeFilterConfig({
              id: 'due_date',
              label: 'Due Date',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 'created_at',
              label: 'Created Date',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 'last_detected_date',
              label: 'Last Detected',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 'last_tested_date',
              label: 'Last Tested',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 'last_fixed_datetime',
              label: 'Fixed Date',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 'last_reopened_datetime',
              label: 'Reopened',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 'ignored_date',
              label: 'Ignored',
              labelWidth: '100px',
            }),
            new DateRangeFilterConfig({
              id: 'unignored_date',
              label: 'Unignored',
              labelWidth: '100px',
            }),
          ],
        }),
        new MultiSelectFilterConfig({
          id: 'severity',
          label: 'Severity',
          tooltip: 'Filter by severity level (multi select).',
          iconClass: 'fas fa-exclamation-triangle', // Icon added
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            { label: 'Urgent', value: '5' },
            { label: 'Critical', value: '4' },
            { label: 'Serious', value: '3' },
            { label: 'Medium', value: '2' },
            { label: 'Minimal', value: '1' },
          ],
          renderStyle: MultiSelectRenderStyle.Badges, // Render as badges
        }),
        new MultiSelectFilterConfig({
          id: 'status',
          label: 'Status',
          tooltip: (
            <>
              <span>Filter by status (multi select).</span>
              <br />
              <span>
                NOTE: Some options are mutually exclusive and cannot be selected
                together.
              </span>
            </>
          ),
          iconClass: 'fas fa-clipboard-check', // Icon added
          defaultSelectedValues: [''],
          options: [
            { label: 'All', value: '', deselectOthers: true },
            {
              label: 'All Open',
              value: 'NEW,ACTIVE,REOPENED',
              deselectOthers: true,
            },
            {
              label: 'All Resolved',
              value: 'FIXED,IGNORED',
              deselectOthers: true,
            },
            { label: 'New (last scan)', value: 'NEW' },
            { label: 'Active', value: 'ACTIVE' },
            { label: 'Reopened (last scan)', value: 'REOPENED' },
            { label: 'Fixed', value: 'FIXED' },
            {
              label: 'Ignored (All)',
              value: 'IGNORED',
              deselectSpecific: ['MANUAL_IGNORED', 'AUTO_IGNORED'],
            },
            {
              label: 'Manual Ignored',
              value: 'MANUAL_IGNORED',
              deselectSpecific: ['IGNORED', 'AUTO_IGNORED'],
            },
            {
              label: 'Auto Ignored',
              value: 'AUTO_IGNORED',
              deselectSpecific: ['IGNORED', 'MANUAL_IGNORED'],
            },
          ],
          renderStyle: MultiSelectRenderStyle.Badges, // Render as badges
        }),
        new MultiSelectFilterConfig({
          id: 'predefinedFilters',
          label: 'Predefined Filters',
          tooltip: 'Quick predefined filters.',
          iconClass: 'fas fa-filter',
          defaultSelectedValues: [''],
          options: [
            { value: '', label: 'All', deselectOthers: true },
            { value: 'severity=5,4', label: 'High Severity' },
            { value: 'vunlassigneemap_set__is_active=true', label: 'Assigned' },
            { value: 'self_assigned=true', label: 'Assigned To Me' },
            { value: 'self_watching=true', label: 'Watching' },
            ...(sourceType !== SourceType.SAST && sourceType !== SourceType.WAS
              ? [
                  {
                    value: 'knowledge_base__patchable=1',
                    label: 'Patchable',
                    deselectSpecific: ['knowledge_base__patchable=0'],
                  },
                  {
                    value: 'knowledge_base__patchable=0',
                    label: 'Non Patchable',
                    deselectSpecific: ['knowledge_base__patchable=1'],
                  },
                  { value: 'title__icontains=Obsolete', label: 'Obsolete' },
                  { value: 'zero_day=true', label: 'Zero Day' },
                ]
              : []),
          ],
          renderStyle: MultiSelectRenderStyle.Badges, // Render as badges
        }),
        new MultiSelectFilterConfig({
          id: 'issue_type',
          label: 'SAST Issue Type',
          tooltip: 'Filter by SAST issue type.',
          iconClass: 'fas fa-bug', // Icon added
          defaultSelectedValues: [''],
          enabled:
            sourceType == SourceType.SAST || sourceType == SourceType.ALL,
          options: [
            { label: 'All', value: '', deselectOthers: true },
            {
              label: 'Vulnerabilities',
              value: 'VULNERABILITY',
              deselectOthers: true,
            },
            { label: 'Bugs', value: 'BUG', deselectOthers: true },
          ],
          renderStyle: MultiSelectRenderStyle.Badges, // Render as badges
        }),
        new AgeFilterConfig({
          id: 'age',
          label: 'Age',
          tooltip: 'Filter by age of the vulnerabilities.',
          iconClass: 'fas fa-clock', // Icon added
          debounceTime: 1000,
          enableDebounce: true,
        }),
        // hidden in menu bar
        new InputFilterConfig({
          id: 'id',
          label: 'ID',
          tooltip: 'Filter by ID',
          renderLabel: true,
          placeholder: ' ',
          debounceTime: 1000,
          enableDebounce: true,
          labelWidth: '65px',
          inputProps: { type: 'text', width: '50px', maxwidth: '100px' },
          showInFilterMenu: false,
        }),
      ],
    });

    // Apply initial filter values
    newConfig.applyInitialFilterValues(filterValues);

    return newConfig;
  }, [filterValues, sourceType]);

  return config;
};
